import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaBullhorn, FaSearch, FaCheck, FaExclamationTriangle } from "react-icons/fa";
import { addFPOBroadcast } from "../../services/fpoBroadcast";
import { getFPOTemplates } from "../../services/fpoTemplate";
import { getFPOMembers, getFPOMemberLocations } from "../../services/fpoMember";
import PageHeader from "../../components/header/PageHeader";
import SearchableSelect from "../../components/inputFields/SearchableSelect";
import { CHANNEL_OPTIONS, AUDIENCE_OPTIONS } from "./shared";

const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary";

const Section = ({ step, title, hint, children }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5">
        <div className="flex items-center gap-2.5 mb-4">
            <span className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                {step}
            </span>
            <h3 className="font-bold text-gray-800 text-sm">{title}</h3>
            {hint && <span className="text-xs text-gray-400">— {hint}</span>}
        </div>
        {children}
    </div>
);

const NewBroadcast = () => {
    const navigate = useNavigate();

    const [channel, setChannel]     = useState("app_push");
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [title, setTitle]         = useState("");
    const [messageBody, setMessageBody] = useState("");

    const [audienceType, setAudienceType] = useState("all_members");
    const [village, setVillage]           = useState("");
    const [villages, setVillages]         = useState([]);

    const [memberSearch, setMemberSearch] = useState("");
    const [memberOptions, setMemberOptions] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState([]); // [{id, name, mobile_number, village}]

    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [topError, setTopError]       = useState(null);

    // Load templates (app_push only, since that's the only wired channel)
    useEffect(() => {
        getFPOTemplates({ channel: "app_push", page_size: 100 })
            .then((res) => {
                const data = res?.data;
                if (data?.status === 200 || data?.status === "200") setTemplates(data.results || []);
            })
            .catch(() => {});
    }, []);

    // Load village options for the audience picker
    useEffect(() => {
        getFPOMemberLocations()
            .then((res) => {
                const data = res?.data;
                if (data?.status === 200 || data?.status === "200") setVillages(data.data?.villages || []);
            })
            .catch(() => {});
    }, []);

    // Debounced member search for the "Custom" audience picker
    useEffect(() => {
        if (audienceType !== "custom") return;
        setMembersLoading(true);
        const t = setTimeout(() => {
            getFPOMembers({ search: memberSearch.trim() || undefined, status: "approved", is_active: true, page_size: 20 })
                .then((res) => {
                    const data = res?.data;
                    if (data?.status === 200 || data?.status === "200") setMemberOptions(data.results || []);
                })
                .catch(() => {})
                .finally(() => setMembersLoading(false));
        }, 350);
        return () => clearTimeout(t);
    }, [memberSearch, audienceType]);

    const handleTemplateChange = (id) => {
        setTemplateId(id);
        const tpl = templates.find((t) => t.id === id);
        if (tpl) {
            setTitle(tpl.title || "");
            setMessageBody(tpl.body || "");
        }
    };

    const toggleMember = (m) => {
        setSelectedMembers((prev) => {
            const exists = prev.some((p) => p.id === m.id);
            if (exists) return prev.filter((p) => p.id !== m.id);
            return [...prev, m];
        });
    };

    const handleSubmit = async () => {
        setFieldErrors({});
        setTopError(null);

        const payload = {
            channel,
            audience_type: audienceType,
        };
        if (templateId) payload.template = templateId;
        if (title.trim()) payload.title = title.trim();
        if (!templateId || messageBody.trim()) payload.message_body = messageBody.trim();
        if (audienceType === "village") payload.village = village;
        if (audienceType === "custom") payload.member_ids = selectedMembers.map((m) => m.id);

        setSubmitting(true);
        try {
            const res  = await addFPOBroadcast(payload);
            const data = res?.data;
            const ok   = data?.status === 200 || data?.status === 201 || res?.status === 200 || res?.status === 201;
            if (ok) {
                toast.success("Broadcast created. Recipients queued for dispatch. ✅");
                navigate(`/notifications/${data.data?.id}`);
            } else {
                setFieldErrors(data?.errors || {});
                setTopError(!data?.errors ? (data?.message || "Could not create broadcast.") : null);
            }
        } catch (e) {
            const data = e?.response?.data;
            setFieldErrors(data?.errors || {});
            setTopError(!data?.errors ? (data?.message || "Something went wrong.") : null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="w-full max-w-3xl pb-12">
            <PageHeader title="New Broadcast" />

            {topError && (
                <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-100 text-amber-800 text-sm rounded-xl px-4 py-3 mb-5">
                    <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
                    <p>{topError}</p>
                </div>
            )}

            {/* ── Step 1: Channel ── */}
            <Section step={1} title="Channel">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {CHANNEL_OPTIONS.map((c) => (
                        <button
                            key={c.value}
                            type="button"
                            disabled={!c.enabled}
                            onClick={() => setChannel(c.value)}
                            className={`relative text-left p-3.5 rounded-xl border-2 transition-colors ${
                                channel === c.value
                                    ? "border-primary bg-primary/5"
                                    : "border-gray-200 hover:border-gray-300"
                            } ${!c.enabled ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                            <div className="flex items-center gap-2 font-semibold text-sm text-gray-800">
                                <c.icon className="text-primary" size={13} />
                                {c.label}
                            </div>
                            {!c.enabled && (
                                <span className="inline-block mt-1 text-[10px] bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full font-semibold">
                                    Coming soon
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </Section>

            {/* ── Step 2: Template ── */}
            <Section step={2} title="Template" hint="optional — pick a saved template or write a custom message">
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => handleTemplateChange("")}
                        className={`px-4 py-2 rounded-xl text-sm font-medium border-2 transition-colors ${
                            !templateId ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                    >
                        ✎ Custom message
                    </button>
                    {templates.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => handleTemplateChange(t.id)}
                            className={`px-4 py-2 rounded-xl text-sm font-medium border-2 transition-colors ${
                                templateId === t.id ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-gray-600 hover:border-gray-300"
                            }`}
                        >
                            {t.code} · {t.name}
                        </button>
                    ))}
                </div>
                {templates.length === 0 && (
                    <p className="text-xs text-gray-400 mt-3">No App Push templates yet — write a custom message below, or create one under Templates.</p>
                )}
            </Section>

            {/* ── Step 3: Message ── */}
            <Section step={3} title="Message" hint={templateId ? "editable before sending" : undefined}>
                <div className="flex flex-col gap-4">
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Title</label>
                        <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="e.g. Soybean procurement — 20 Jul" />
                        {fieldErrors.title && <p className="text-red-500 text-xs mt-1">{fieldErrors.title[0]}</p>}
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Body{!templateId && <span className="text-red-500 ml-0.5">*</span>}
                        </label>
                        <textarea
                            value={messageBody}
                            onChange={(e) => setMessageBody(e.target.value)}
                            rows={4}
                            className={inputCls}
                            placeholder="Namaskar {farmer_name}! …"
                        />
                        {fieldErrors.message_body && <p className="text-red-500 text-xs mt-1">{fieldErrors.message_body[0]}</p>}
                        <p className="text-[11px] text-gray-400 mt-1">
                            Insert: {"{farmer_name}"} {"{commodity}"} {"{date}"} {"{rate}"} {"{amount}"}
                        </p>
                    </div>
                </div>
            </Section>

            {/* ── Step 4: Audience ── */}
            <Section step={4} title="Audience">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    {AUDIENCE_OPTIONS.map((a) => (
                        <button
                            key={a.value}
                            type="button"
                            onClick={() => setAudienceType(a.value)}
                            className={`text-left p-3.5 rounded-xl border-2 transition-colors font-semibold text-sm ${
                                audienceType === a.value ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-gray-700 hover:border-gray-300"
                            }`}
                        >
                            {a.label}
                        </button>
                    ))}
                </div>

                {audienceType === "village" && (
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Village</label>
                        <SearchableSelect
                            value={village}
                            onChange={setVillage}
                            placeholder="— Select village —"
                            options={villages.map((v) => ({ value: v, label: v }))}
                            className={inputCls}
                        />
                        {fieldErrors.village && <p className="text-red-500 text-xs mt-1">{fieldErrors.village[0]}</p>}
                    </div>
                )}

                {audienceType === "custom" && (
                    <div>
                        <div className="relative mb-3">
                            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
                            <input
                                value={memberSearch}
                                onChange={(e) => setMemberSearch(e.target.value)}
                                placeholder="Search approved members by name, mobile, code…"
                                className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary bg-gray-50"
                            />
                        </div>

                        {selectedMembers.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                                {selectedMembers.map((m) => (
                                    <span key={m.id} className="inline-flex items-center gap-1.5 text-xs font-medium bg-primary/10 text-primary px-2.5 py-1 rounded-full">
                                        {m.name}
                                        <button type="button" onClick={() => toggleMember(m)} className="hover:text-red-500">×</button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="border border-gray-200 rounded-xl max-h-56 overflow-y-auto divide-y divide-gray-50">
                            {membersLoading ? (
                                <p className="text-center text-xs text-gray-400 py-6">Loading…</p>
                            ) : memberOptions.length === 0 ? (
                                <p className="text-center text-xs text-gray-400 py-6">No matching members.</p>
                            ) : (
                                memberOptions.map((m) => {
                                    const checked = selectedMembers.some((p) => p.id === m.id);
                                    return (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => toggleMember(m)}
                                            className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-800 truncate">{m.name}</p>
                                                <p className="text-[11px] text-gray-400">
                                                    {[m.member_code, m.mobile_number, m.village].filter(Boolean).join(" · ")}
                                                </p>
                                            </div>
                                            <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                                                checked ? "bg-primary border-primary text-white" : "border-gray-300"
                                            }`}>
                                                {checked && <FaCheck size={10} />}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                        {fieldErrors.member_ids && <p className="text-red-500 text-xs mt-1">{fieldErrors.member_ids[0]}</p>}
                        <p className="text-[11px] text-gray-400 mt-2">{selectedMembers.length} member{selectedMembers.length === 1 ? "" : "s"} selected</p>
                    </div>
                )}
            </Section>

            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={() => navigate("/notifications")}
                    className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={submitting}
                    onClick={handleSubmit}
                    className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-green-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                >
                    <FaBullhorn size={12} />
                    {submitting ? "Sending…" : "Send Broadcast"}
                </button>
            </motion.div>
        </div>
    );
};

export default NewBroadcast;
